import React, { useEffect, useMemo, useRef, useState } from "react"
import styles from "./duplicateQueue.module.css"
import Button from "../../components/Form/Button"
import Select from "../../components/Form/Select"
import Textarea from "../../components/Form/Textarea"
import ToggleField from "../../components/Form/ToggleField"
import InfoTooltip from "../../components/InfoTooltip"
import Loading from "../layouts/Loading"
import Modal from "../../components/Modal"
import api from "../../services/api"
import { toast } from "react-toastify"
import { MdArrowBack, MdArrowForward, MdCheckCircle, MdMerge, MdOutlineArchive, MdOutlineCompareArrows, MdOutlineVisibility, MdWarningAmber } from "react-icons/md"
import { useCrm } from "../../contexts/crmContext"
import { useAuth } from "../../contexts/authContext"

const reasonOptions = [
    { id: "same_person_or_company", value: "Mesmo lead / mesma empresa" },
    { id: "shared_contact", value: "Contato compartilhado" },
    { id: "different_people_or_company", value: "Pessoas ou empresas diferentes" },
    { id: "false_positive", value: "Falso positivo" },
    { id: "insufficient_data", value: "Dados insuficientes" },
    { id: "legacy_or_imported_data", value: "Base importada / legado" },
    { id: "other", value: "Outro motivo" },
]

const scopeOptions = [
    { id: "group", value: "Somente este grupo atual" },
    { id: "identifier", value: "Este identificador também no futuro" },
]

const DuplicateReviewModal = ({ candidate, onClose, onResolved }) => {
    const [loading, setLoading] = useState(false)
    const [details, setDetails] = useState(null)
    const [selectedPrimary, setSelectedPrimary] = useState("")
    const [selectedDuplicates, setSelectedDuplicates] = useState([])
    const [confirmMergeImpact, setConfirmMergeImpact] = useState(false)
    const [highlightConfirm, setHighlightConfirm] = useState(false)
    const [currentStep, setCurrentStep] = useState("primary")
    const [reasonCategory, setReasonCategory] = useState("")
    const [reasonText, setReasonText] = useState("")
    const [decisionScope, setDecisionScope] = useState("group")
    const [mergePreview, setMergePreview] = useState(null)
    const [previewLoading, setPreviewLoading] = useState(false)
    const confirmRef = useRef(null)
    const { openModalLead } = useCrm()
    const { permissions } = useAuth()
    const duplicatePermission = Number(permissions?.duplicates || 0)
    const canReviewDuplicates = duplicatePermission >= 2
    const canMergeDuplicates = duplicatePermission >= 3
    const canIgnoreIdentifier = duplicatePermission >= 3

    useEffect(() => {
        if (candidate) loadCandidate()
    }, [candidate])

    useEffect(() => {
        if (!candidate || !details || !selectedPrimary) return
        loadMergePreview()
    }, [candidate, details, selectedPrimary, selectedDuplicates])

    const selectedMergeImpact = useMemo(() => {
        const leads = details?.leads || []
        const primary = leads.find(lead => lead._id === selectedPrimary)
        const archiveLeads = leads.filter(lead => selectedDuplicates.includes(lead._id))
        const preview = mergePreview || details?.merge_preview || {}
        const previewArchiveIds = (preview.archive_leads || []).map(lead => lead._id).sort().join(",")
        const selectedArchiveIds = [...selectedDuplicates].sort().join(",")
        const previewIsCurrent = preview.primary_lead_id === selectedPrimary && previewArchiveIds === selectedArchiveIds
        const relatedLabels = {
            tasks: "Tarefas",
            notes: "Notas",
            sales: "Vendas",
            discards: "Descartes",
            historics: "Históricos",
            events: "Eventos",
            conversations: "Conversas",
        }
        const related = Object.entries(archiveLeads.reduce((acc, lead) => {
            Object.entries(lead.merge_counts || {}).forEach(([key, value]) => {
                acc[key] = (acc[key] || 0) + (Number(value) || 0)
            })
            return acc
        }, {})).map(([id, value]) => ({ id, name: relatedLabels[id] || id, value }))
        return {
            primary,
            archiveLeads,
            archive_count: archiveLeads.length,
            inherited: previewIsCurrent ? (preview.inherited || []) : [],
            warnings: previewIsCurrent ? (preview.warnings || []) : [],
            related,
            previewIsCurrent,
        }
    }, [details, mergePreview, selectedPrimary, selectedDuplicates])

    const unselectedDuplicateCount = Math.max(0, (details?.leads || []).filter(lead => lead._id !== selectedPrimary).length - selectedDuplicates.length)

    const loadCandidate = async () => {
        try {
            setDetails(null)
            setSelectedPrimary("")
            setSelectedDuplicates([])
            setConfirmMergeImpact(false)
            setCurrentStep("primary")
            setReasonCategory("")
            setReasonText("")
            setDecisionScope("group")
            setMergePreview(null)
            setLoading(true)
            const queryString = new URLSearchParams({ type: candidate.type, value: candidate.value }).toString()
            const response = await api.get(`/leads/duplicates/candidates?${queryString}`)
            const data = response.data?.data || {}
            setDetails(data)
            const firstLeadId = data?.leads?.[0]?._id
            setSelectedPrimary(firstLeadId || "")
            setSelectedDuplicates((data?.leads || []).slice(1).map(lead => lead._id))
            setMergePreview(data.merge_preview || null)
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível carregar os leads duplicados.")
            onClose()
        } finally {
            setLoading(false)
        }
    }

    const loadMergePreview = async () => {
        try {
            setPreviewLoading(true)
            const queryString = new URLSearchParams({
                type: candidate.type,
                value: candidate.value,
                primary_lead_id: selectedPrimary,
                duplicate_lead_ids: selectedDuplicates.join(","),
            }).toString()
            const response = await api.get(`/leads/duplicates/candidates?${queryString}`)
            setMergePreview(response.data?.data?.merge_preview || null)
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível recalcular a prévia da mesclagem.")
        } finally {
            setPreviewLoading(false)
        }
    }

    const handleChangePrimary = (leadId) => {
        setSelectedPrimary(leadId)
        setConfirmMergeImpact(false)
        setHighlightConfirm(false)
        setSelectedDuplicates(previousValue => previousValue.filter(id => id !== leadId))
    }

    const handleToggleDuplicate = (leadId) => {
        if (leadId === selectedPrimary) return
        setConfirmMergeImpact(false)
        setHighlightConfirm(false)
        setSelectedDuplicates(previousValue => previousValue.includes(leadId)
            ? previousValue.filter(id => id !== leadId)
            : [...previousValue, leadId]
        )
    }

    const handleResolveDuplicate = async (action) => {
        try {
            if (action === "merged" && (!selectedPrimary || !selectedDuplicates.length)) {
                return toast.error("Selecione o lead principal e ao menos um duplicado para mesclar.")
            }
            if (!canReviewDuplicates) {
                return toast.error("Você não possui permissão para revisar duplicidades.")
            }
            if (action === "merged" && !canMergeDuplicates) {
                return toast.error("Você não possui permissão para mesclar duplicidades.")
            }
            if (action !== "merged" && decisionScope === "identifier" && !canIgnoreIdentifier) {
                return toast.error("Você não possui permissão para ignorar identificadores futuros.")
            }
            if (!reasonCategory) {
                return toast.error("Selecione o motivo da decisão.")
            }
            if (!confirmMergeImpact) {
                setHighlightConfirm(true)
                confirmRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
                setTimeout(() => setHighlightConfirm(false), 3500)
                return toast.error("Confirme que revisou o impacto antes de concluir.")
            }
            const groupDuplicateIds = (details?.leads || [])
                .filter(lead => lead._id !== selectedPrimary)
                .map(lead => lead._id)
            setLoading(true)
            await api.post("/leads/duplicates/resolve", {
                type: candidate.type,
                value: candidate.value,
                action,
                primary_lead_id: selectedPrimary,
                duplicate_lead_ids: action === "merged" ? selectedDuplicates : groupDuplicateIds,
                decision_scope: action === "merged" ? "selection" : decisionScope,
                reason_category: reasonCategory,
                reason: reasonText,
            })
            toast.success("Duplicidade revisada com sucesso.")
            onResolved?.()
            onClose()
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível revisar a duplicidade.")
        } finally {
            setLoading(false)
        }
    }

    const formatDate = (value) => {
        if (!value) return "-"
        return new Date(value).toLocaleDateString("pt-BR")
    }

    const getStatusLabel = (lead = {}) => {
        if (lead.status === "won") return "Venda registrada"
        if (lead.status === "lost" || lead.discarted) return "Descartado"
        if (lead.status === "deleted") return "Na lixeira"
        return "Em atendimento"
    }

    const getReviewActionLabel = (action) => ({
        merged: "Mesclado",
        kept_separate: "Mantido separado",
        ignored: "Ignorado",
    })[action] || action || "Revisado"

    const getReasonLabel = (reason) => reasonOptions.find(option => option.id === reason)?.value || reason

    const getScopeLabel = (scope) => ({
        selection: "seleção",
        group: "grupo atual",
        identifier: "identificador futuro",
    })[scope] || scope

    const getOwnerLabel = (lead = {}) => {
        const owners = Array.isArray(lead.owners) ? lead.owners : []
        if (owners.length) return owners.map(owner => owner.name).filter(Boolean).join(", ")
        return lead.users_count ? `${lead.users_count} responsável(is)` : "Sem responsável"
    }

    const getTotalRecords = (lead = {}) => Object.values(lead.merge_counts || {}).reduce((total, value) => total + (Number(value) || 0), 0)

    if (!candidate) return null

    const leads = details?.leads || []
    const availableScopeOptions = canIgnoreIdentifier ? scopeOptions : scopeOptions.filter(option => option.id !== "identifier")
    const reviewSteps = [
        {
            id: "primary",
            title: "Escolha o cadastro principal",
            description: "Este lead continuará ativo e receberá os dados preservados dos duplicados.",
            icon: <MdCheckCircle />,
        },
        {
            id: "duplicates",
            title: "Escolha quem será mesclado",
            description: "Marque apenas os cadastros que devem ser arquivados se a decisão final for mesclar.",
            icon: <MdOutlineArchive />,
        },
        {
            id: "confirm",
            title: "Revise e conclua",
            description: "Confira o impacto antes de mesclar, manter separado ou ignorar o grupo.",
            icon: <MdMerge />,
        },
    ]
    const currentStepIndex = reviewSteps.findIndex(step => step.id === currentStep)
    const currentStepData = reviewSteps[currentStepIndex] || reviewSteps[0]

    const renderTooltipLabel = (label, text) => (
        <span className={styles.tooltipLabel}>
            {label}
            <InfoTooltip text={text} preferredPosition="bottom" />
        </span>
    )

    const handleNextStep = () => {
        if (currentStep === "primary" && !selectedPrimary) {
            return toast.error("Escolha qual cadastro será mantido como principal.")
        }
        const nextStep = reviewSteps[currentStepIndex + 1]
        if (nextStep) setCurrentStep(nextStep.id)
    }

    const handlePreviousStep = () => {
        const previousStep = reviewSteps[currentStepIndex - 1]
        if (previousStep) setCurrentStep(previousStep.id)
    }

    return (
        <Modal
            icon={<MdMerge />}
            title="Revisar duplicidade"
            description={`${candidate.type_label}: ${candidate.value}`}
            closeModal={onClose}
            fullWidith={true}
            heightFitContent={true}
        >
            <div className={styles.reviewWizard}>
                <aside className={styles.wizardAside}>
                    <div className={styles.reviewSummary}>
                        <span>Identificador duplicado</span>
                        <strong>{candidate.type_label}: {candidate.value}</strong>
                    </div>

                    <div className={styles.reviewStats}>
                        <div>
                            <strong>{leads.length}</strong>
                            <span>cadastros encontrados</span>
                        </div>
                        <div>
                            <strong>{selectedMergeImpact.archive_count}</strong>
                            <span>selecionados para mesclar</span>
                        </div>
                    </div>

                    <div className={styles.wizardSteps}>
                        {reviewSteps.map((step, index) => (
                            <button
                                className={`${styles.wizardStep} ${currentStep === step.id ? styles.currentWizardStep : ""} ${index < currentStepIndex ? styles.completedWizardStep : ""}`}
                                key={step.id}
                                type="button"
                                onClick={() => setCurrentStep(step.id)}
                            >
                                <span className={styles.stepIcon}>{step.icon}</span>
                                <span>
                                    <strong>{index + 1}. {step.title}</strong>
                                    <small>{step.description}</small>
                                </span>
                            </button>
                        ))}
                    </div>
                </aside>

                <main className={styles.wizardMain}>
                    <div className={styles.wizardHeader}>
                        <span>Etapa {currentStepIndex + 1} de {reviewSteps.length}</span>
                        <h3>{currentStepData.title}</h3>
                        <p>{currentStepData.description}</p>
                        {currentStep === "duplicates" &&
                            <p className={styles.wizardHint}>Se este identificador não for uma duplicidade real, avance sem alterar a seleção e use “Manter separados” ou “Ignorar grupo” na última etapa.</p>
                        }
                    </div>

                    {currentStep === "primary" &&
                        <div className={styles.leadSelectionList}>
                            {leads.map(lead => (
                                <div
                                    key={lead._id}
                                    className={`${styles.selectLeadRow} ${selectedPrimary === lead._id ? styles.selectedLeadRow : ""}`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => handleChangePrimary(lead._id)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") handleChangePrimary(lead._id)
                                    }}
                                >
                                    <span className={styles.radioMark}>{selectedPrimary === lead._id && <MdCheckCircle />}</span>
                                    <span className={styles.selectLeadInfo}>
                                        <strong>{lead.name}</strong>
                                        <small>{getStatusLabel(lead)} · Atualizado em {formatDate(lead.updated_at)}</small>
                                        <span className={styles.leadContextGrid}>
                                            <span><small>Responsável</small><strong>{getOwnerLabel(lead)}</strong></span>
                                            <span><small>Equipe</small><strong>{lead.team_name || "-"}</strong></span>
                                            <span><small>Funil</small><strong>{lead.funnel_name || "-"}</strong></span>
                                            <span><small>Etapa</small><strong>{lead.stage_name || "-"}</strong></span>
                                            <span><small>Contato</small><strong>{lead.phone || lead.email || "-"}</strong></span>
                                        </span>
                                    </span>
                                    <span className={styles.selectLeadMeta}>
                                        <small>Histórico</small>
                                        <strong>{getTotalRecords(lead)}</strong>
                                    </span>
                                    <span className={styles.selectLeadMeta}>
                                        <small>Produtos</small>
                                        <strong>{lead.products_count || 0}</strong>
                                    </span>
                                    <button className={styles.inlineLeadButton} type="button" onClick={(event) => {
                                        event.stopPropagation()
                                        openModalLead(lead._id)
                                    }}>
                                        <MdOutlineVisibility /> Abrir
                                    </button>
                                </div>
                            ))}
                        </div>
                    }

                    {currentStep === "duplicates" &&
                        <div className={styles.leadSelectionList}>
                            {leads.map(lead => {
                                const isPrimary = selectedPrimary === lead._id
                                const isSelected = selectedDuplicates.includes(lead._id)

                                return (
                                    <div
                                        key={lead._id}
                                        className={`${styles.selectLeadRow} ${isSelected ? styles.selectedLeadRow : ""} ${isPrimary ? styles.lockedLeadRow : ""}`}
                                        role="button"
                                        tabIndex={isPrimary ? -1 : 0}
                                        onClick={() => handleToggleDuplicate(lead._id)}
                                        onKeyDown={(event) => {
                                            if (!isPrimary && (event.key === "Enter" || event.key === " ")) handleToggleDuplicate(lead._id)
                                        }}
                                    >
                                        <span className={styles.checkboxMark}>{isSelected && <MdCheckCircle />}</span>
                                        <span className={styles.selectLeadInfo}>
                                            <strong>{lead.name}</strong>
                                            <small>{isPrimary ? "Cadastro principal escolhido na etapa anterior" : `${lead.phone || "Sem telefone"} · ${lead.email || "Sem e-mail"}`}</small>
                                        </span>
                                        <span className={styles.selectLeadMeta}>
                                            <small>Histórico</small>
                                            <strong>{getTotalRecords(lead)}</strong>
                                        </span>
                                        <button className={styles.inlineLeadButton} type="button" onClick={(event) => {
                                            event.stopPropagation()
                                            openModalLead(lead._id)
                                        }}>
                                            <MdOutlineVisibility /> Abrir
                                        </button>
                                    </div>
                                )
                            })}
                        </div>
                    }

                    {currentStep === "confirm" &&
                        <div className={styles.confirmStep}>
                            <div className={styles.impactCard}>
                                <div className={styles.impactHeader}>
                                    <div>
                                        <span className={styles.impactLabel}>Resultado se você mesclar</span>
                                        <strong>{selectedMergeImpact.archive_count} cadastro(s) selecionado(s) serão arquivados e o principal continuará ativo.</strong>
                                    </div>
                                    <MdOutlineCompareArrows />
                                </div>
                                {unselectedDuplicateCount > 0 &&
                                    <div className={styles.partialNotice}>
                                        <MdWarningAmber />
                                        <span>
                                            <strong>Seleção parcial</strong>
                                            {unselectedDuplicateCount} cadastro(s) deste grupo não serão mesclados agora. Se ainda houver duplicidade ativa após a decisão, o identificador voltará para a fila.
                                            <InfoTooltip text="Use seleção parcial quando apenas alguns cadastros são realmente o mesmo lead. Os demais continuam ativos e podem ser revisados depois." preferredPosition="bottom" />
                                        </span>
                                    </div>
                                }
                                <div className={styles.impactGrid}>
                                    <div>
                                        <span className={styles.impactLabel}>Cadastro que será mantido</span>
                                        <strong>{selectedMergeImpact.primary?.name || "-"}</strong>
                                    </div>
                                    <div>
                                        <span className={styles.impactLabel}>Cadastros que serão arquivados</span>
                                        <strong>{selectedMergeImpact.archiveLeads.map(lead => lead.name).join(", ") || "-"}</strong>
                                    </div>
                                </div>
                                <div className={styles.previewDeltaPanel}>
                                    <div className={styles.previewDeltaHeader}>
                                        <div>
                                            <span className={styles.impactLabel}>Prévia antes/depois</span>
                                            <strong>{previewLoading ? "Recalculando impacto..." : "Dados que serão consolidados no lead principal."}</strong>
                                        </div>
                                    </div>
                                    <div className={styles.previewDeltaGrid}>
                                        {(selectedMergeImpact.inherited || []).map(item => (
                                            <div className={styles.previewDeltaItem} key={item.id}>
                                                <span>{item.name}</span>
                                                <div>
                                                    <strong>{item.before}</strong>
                                                    <small>antes</small>
                                                </div>
                                                <MdArrowForward />
                                                <div>
                                                    <strong>{item.after}</strong>
                                                    <small>depois</small>
                                                </div>
                                                {item.added > 0 &&
                                                    <em>+{item.added}</em>
                                                }
                                            </div>
                                        ))}
                                        {!selectedMergeImpact.inherited?.length &&
                                            <span className={styles.selectionHelp}>Selecione os duplicados para ver a prévia de consolidação.</span>
                                        }
                                    </div>
                                </div>
                                {selectedMergeImpact.related.length > 0 &&
                                    <div className={styles.impactMetrics}>
                                        {selectedMergeImpact.related.slice(0, 6).map(item => (
                                            <div className={styles.impactMetric} key={item.id}>
                                                <span className={styles.impactLabel}>{item.name}</span>
                                                <strong>{item.value}</strong>
                                            </div>
                                        ))}
                                    </div>
                                }
                                {selectedMergeImpact.warnings.length > 0 &&
                                    <div className={styles.warningList}>
                                        {selectedMergeImpact.warnings.map((warning, index) => (
                                            <span key={`${warning}-${index}`}><MdWarningAmber /> {warning}</span>
                                        ))}
                                    </div>
                                }
                                {!selectedMergeImpact.previewIsCurrent &&
                                    <span className={styles.selectionHelp}>Os totais herdados serão recalculados para a seleção atual.</span>
                                }
                                <div className={styles.reviewDecisionForm}>
                                    <div>
                                        {renderTooltipLabel("Motivo da decisão", "Escolha por que esta revisão está sendo concluída. Isso ajuda auditoria, relatórios e futuras automações a entenderem o padrão.")}
                                        <Select name="Motivo da decisão" placeholder="Pesquise um motivo" options={reasonOptions} selected={reasonCategory} onChange={setReasonCategory} />
                                    </div>
                                    <div>
                                        {renderTooltipLabel("Escopo para manter/ignorar", "Grupo atual afeta só os leads que você está vendo agora. Identificador futuro evita novos alertas para o mesmo telefone, e-mail ou documento.")}
                                        <Select name="Escopo da decisão" placeholder="Pesquise um escopo" options={availableScopeOptions} selected={decisionScope} onChange={setDecisionScope} allowDeselectAll={false} />
                                    </div>
                                    <div className={styles.reviewDecisionNote}>
                                        {renderTooltipLabel("Observação opcional", "Use quando o motivo precisar de contexto, como telefone da matriz, contato de familiar ou importação antiga.")}
                                        <Textarea value={reasonText} onChange={(event) => setReasonText(event.target.value)} placeholder="Ex.: telefone da matriz, contato de familiar, lead importado em duplicidade..." rows={3} />
                                    </div>
                                </div>
                                <ToggleField
                                    elementRef={confirmRef}
                                    className={`${styles.confirmBox} ${highlightConfirm ? styles.attention : ""}`}
                                    checked={confirmMergeImpact}
                                    onChange={checked => {
                                        setConfirmMergeImpact(checked)
                                        setHighlightConfirm(false)
                                    }}
                                    label="Confirmação obrigatória"
                                    description="Revisei os cadastros, entendi o impacto da decisão e confirmo que posso concluir esta revisão."
                                />
                            </div>

                            <div className={styles.decisionGuide}>
                                <div>
                                    <strong>Mesclar selecionados <InfoTooltip text="Use quando os cadastros selecionados representam o mesmo lead. Os duplicados marcados são arquivados e os históricos são consolidados no principal." preferredPosition="bottom" /></strong>
                                    <span>Arquiva somente os duplicados marcados e consolida histórico no cadastro principal.</span>
                                </div>
                                <div>
                                    <strong>Manter separados <InfoTooltip text="Use quando os cadastros são legítimos e precisam continuar separados, mesmo compartilhando telefone, e-mail ou documento." preferredPosition="bottom" /></strong>
                                    <span>Use quando os cadastros são pessoas/empresas diferentes, mesmo compartilhando o identificador.</span>
                                </div>
                                <div>
                                    <strong>Ignorar grupo <InfoTooltip text="Use quando este alerta não merece ação agora. Com escopo por identificador, ele também deixa de voltar no futuro para o mesmo dado." preferredPosition="bottom" /></strong>
                                    <span>Use quando o alerta não deve voltar para a fila com o identificador atual.</span>
                                </div>
                            </div>

                            {details?.reviews?.length > 0 &&
                                <div className={styles.historyPanel}>
                                    <strong>Histórico deste identificador</strong>
                                    <small>Decisões anteriores registradas para este mesmo {candidate.type_label?.toLowerCase()}.</small>
                                    {details.reviews.map(review => (
                                        <span key={review._id}>
                                            {getReviewActionLabel(review.action)} em {formatDate(review.created_at)}
                                            {review.reason_category ? ` · ${getReasonLabel(review.reason_category)}` : ""}
                                            {review.decision_scope ? ` · ${getScopeLabel(review.decision_scope)}` : ""}
                                        </span>
                                    ))}
                                </div>
                            }
                        </div>
                    }

                    <div className={styles.wizardFooter}>
                        <div>
                            {currentStepIndex > 0 &&
                                <Button variant="neutral" type="button" onClick={handlePreviousStep} disabled={loading}>
                                    <MdArrowBack /> Voltar
                                </Button>
                            }
                        </div>
                        {currentStep !== "confirm" ?
                            <Button variant="secondary" type="button" onClick={handleNextStep} disabled={loading}>
                                Avançar <MdArrowForward />
                            </Button>
                            :
                            <div className={styles.finalActions}>
                                <Button variant="neutral" type="button" onClick={() => handleResolveDuplicate("kept_separate")} disabled={loading || !canReviewDuplicates} title={!canReviewDuplicates ? "Sem permissão para revisar duplicidades" : undefined}>
                                    Manter separados
                                </Button>
                                <Button variant="primary" type="button" onClick={() => handleResolveDuplicate("ignored")} disabled={loading || !canReviewDuplicates} title={!canReviewDuplicates ? "Sem permissão para revisar duplicidades" : undefined}>
                                    Ignorar grupo
                                </Button>
                                <Button variant="secondary" type="button" onClick={() => handleResolveDuplicate("merged")} disabled={loading || !selectedDuplicates.length || !canMergeDuplicates} title={!canMergeDuplicates ? "Sem permissão para mesclar duplicidades" : undefined}>
                                    <MdMerge /> Mesclar selecionados
                                </Button>
                            </div>
                        }
                    </div>
                </main>
            </div>
            {loading && <Loading fullPage={true} />}
        </Modal>
    )
}

export default DuplicateReviewModal
